/** Read the fragment once, strip it immediately, then verify through the public body-only endpoint. */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, createApiClient } from "@/api/client";
import { enterRoleMode } from "@/auth/roleMode";
import { parseRoleRedemption, roleDestination, takeRoleCode } from "@/auth/roleEntry";
import { useFeedback } from "@/stores/feedback";
import { useJudgeSession } from "@/stores/judgeSession";
import { useOnline } from "@/hooks/useOnline";
import { judgeCache } from "@/components/judge/cache";
import {
  offlineJudgePointer,
  clearOfflineJudgePointer,
  readOfflineQueue,
} from "@/components/judge/offlineRecovery";
import type { Heat, HeatRider } from "@/types";
import type { QueueItem } from "@/api/offlineQueue";
import type { JudgeResult } from "@/components/judge/model";
import "./RoleAccess.css";
function OfflineJudgeSnapshot() {
  const [pointer] = useState(offlineJudgePointer);
  const [queues, setQueues] = useState<Record<string, QueueItem[]>>({});
  useEffect(() => {
    let active = true;
    if (pointer)
      void readOfflineQueue(pointer)
        .then((data) => {
          if (active) setQueues(data);
        })
        .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [pointer]);
  if (!pointer) return null;
  const cache = judgeCache(pointer.principal, pointer.competitionId);
  if (!cache.read("warm")) return null;
  const base = `/api/competitions/${pointer.competitionId}`;
  const heats = cache.read<Heat[]>(`${base}/heats`) ?? [];
  return (
    <section className="role-offline-snapshot" aria-label="Saved judging data">
      <h1>Saved judging data</h1>
      <p role="status">
        Offline · Read only. Reopen your original role link online before scoring or syncing. Saved
        values may be out of date.
      </p>
      <p>Judge {pointer.judgeNumber}</p>
      {heats.map((heat) => {
        const riders = cache.read<HeatRider[]>(`${base}/heats/${heat.id}/riders`) ?? [];
        const results = cache.read<{ competitors: JudgeResult[] }>(
          `${base}/heats/${heat.id}/scores/results:seat:${pointer.judgeNumber}`,
        );
        return (
          <section key={heat.id}>
            <h2>{heat.name}</h2>
            <ul>
              {riders.map((rider) => (
                <li key={rider.id}>
                  <strong>{rider.rider.name}</strong>
                  <ul>
                    {results?.competitors
                      .find((row) => row.heat_rider_id === rider.id)
                      ?.attempts.map((attempt) => (
                        <li key={attempt.attempt_number}>
                          Attempt {attempt.attempt_number}:{" "}
                          {attempt.judge_scores?.[String(pointer.judgeNumber)] ?? "—"}
                        </li>
                      ))}
                  </ul>
                </li>
              ))}
            </ul>
            <h3>Saved queue</h3>
            <ul>
              {(queues[heat.id] ?? []).map((item) => (
                <li key={item.client_id}>
                  {"heat_rider_id" in item
                    ? riders.find((rider) => rider.id === item.heat_rider_id)?.rider.name
                    : "Placements"}
                  {"attempt_number" in item && ` · Attempt ${item.attempt_number}`} · {item.kind}:{" "}
                  {"value" in item && <strong>{item.value}</strong>}
                  {"criteria" in item &&
                    item.criteria &&
                    Object.entries(item.criteria).map(([criterion, value]) => (
                      <span key={criterion}>
                        {" "}
                        {criterion}: {value}
                      </span>
                    ))}
                  {"ordered_heat_rider_ids" in item &&
                    item.ordered_heat_rider_ids
                      .map(
                        (id) =>
                          riders.find((rider) => rider.id === id)?.rider.name ??
                          "Rider unavailable",
                      )
                      .join(" → ")}
                  {"jam_item" in item && item.jam_item?.label} · {item.state}
                  {item.reason && ` · ${item.reason}`}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </section>
  );
}
const redemption = createApiClient(async () => null);
export function RoleAccessPage() {
  const [code] = useState(() => {
    const value = takeRoleCode();
    if (value) clearOfflineJudgePointer();
    return value;
  });
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(Boolean(code));
  const [needsPin, setNeedsPin] = useState(false);
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);
  const attempted = useRef(false);
  const alive = useRef(true);
  const online = useOnline();
  const navigate = useNavigate();
  async function redeem(value?: string) {
    if (!code || !navigator.onLine) {
      setBusy(false);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const response = await redemption.post<unknown>(
        "/api/judge-access",
        { token: code, ...(value ? { pin: value } : {}) },
        { canRetry: () => false },
      );
      if (!alive.current) return;
      const session = parseRoleRedemption(response);
      useJudgeSession.getState().setVerifiedSession(session);
      navigate(roleDestination(session), { replace: true });
    } catch (cause) {
      if (!alive.current) return;
      if (cause instanceof ApiError && cause.status === 401) {
        // The anonymous endpoint deliberately does not reveal whether a PIN is set.
        // Treat its first challenge as the PIN step; show later failures beside the field.
        const feedback = useFeedback.getState();
        feedback.toasts
          .filter((toast) => toast.key === "api:POST:/api/judge-access")
          .forEach((toast) => feedback.dismiss(toast.id));
        setNeedsPin(true);
        if (value)
          setError(
            "The PIN or link is not valid. Check the PIN or ask the organiser for a new link.",
          );
      } else {
        setLocked(cause instanceof ApiError && cause.status === 429);
        setError(cause instanceof Error ? cause.message : "Could not open this link. Try again.");
      }
    } finally {
      if (alive.current) setBusy(false);
    }
  }
  useEffect(() => {
    alive.current = true;
    enterRoleMode();
    useJudgeSession.getState().clear();
    if (!attempted.current && code) {
      attempted.current = true;
      void redeem();
    }
    return () => {
      alive.current = false;
    };
    // The fragment is consumed once. Retry keeps it only in this mounted component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <main className="role-access">
      <header>
        <strong>Scorio</strong>
      </header>
      <section className="role-access-card">
        {!code && !online && offlineJudgePointer() ? (
          <OfflineJudgeSnapshot />
        ) : !code ? (
          <>
            <h1>Open your role link</h1>
            <p>
              Ask the organiser for your Judge, Starter or Announcer link. Your queued scores stay
              saved.
            </p>
          </>
        ) : !online ? (
          <>
            <h1>Connect to open your role link</h1>
            <p>Your saved judging queue is safe. Reconnect before opening a new session.</p>
            <button
              className="role-access-primary"
              disabled={busy}
              onClick={() => void redeem(pin)}
            >
              Retry
            </button>
          </>
        ) : busy && !needsPin ? (
          <>
            <h1>Opening your role link</h1>
            <div
              className="role-access-skeleton"
              role="status"
              aria-label="Verifying role access"
            />
          </>
        ) : (
          <>
            <h1>
              {locked
                ? "Access temporarily locked"
                : needsPin
                  ? "Enter access PIN"
                  : "Could not open this link"}
            </h1>
            {error && (
              <p className="role-access-error" role="alert">
                {error}
              </p>
            )}
            {!locked && (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void redeem(pin);
                }}
              >
                {needsPin && (
                  <label>
                    Access PIN
                    <input
                      autoFocus
                      autoComplete="off"
                      inputMode="numeric"
                      type="password"
                      value={pin}
                      onChange={(event) => setPin(event.target.value)}
                      required
                      maxLength={32}
                    />
                  </label>
                )}
                <button className="role-access-primary" disabled={busy}>
                  {busy ? "Opening…" : needsPin ? "Continue" : "Retry"}
                </button>
              </form>
            )}
            <p>
              Your role session stays in this tab until you leave. If this link has expired, ask the
              organiser for a new one.
            </p>
          </>
        )}
      </section>
    </main>
  );
}
